import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { WallItem } from "../../shared/types";
import { getMadonnaCards } from "../api";

type CardStyle = CSSProperties & Record<`--${string}`, string>;

const hash = (value: string): number => {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
};

export const randomizeCards = <T extends { id: string }>(items: T[], seed: string): T[] =>
  [...items].sort((left, right) => {
    const order = hash(`${seed}:${left.id}`) - hash(`${seed}:${right.id}`);
    return order || left.id.localeCompare(right.id);
  });

export const randomizeReportCards = <T extends { id: string; name: string; hasImage: boolean }>(
  items: T[],
  seed: string,
  preferredName = "Alex Wolf",
  preferredStartPosition = 5,
  preferredEndPosition = 12,
): T[] => {
  const randomized = randomizeCards(items, seed);
  const normalizedName = preferredName.trim().toLocaleLowerCase();
  const preferredIndex = randomized.findIndex((item) =>
    item.hasImage && item.name.trim().toLocaleLowerCase() === normalizedName);
  if (preferredIndex < 0) return randomized;

  const [preferred] = randomized.splice(preferredIndex, 1);
  const firstIndex = Math.min(Math.max(1, preferredStartPosition) - 1, randomized.length);
  const lastIndex = Math.min(
    Math.max(preferredStartPosition, preferredEndPosition) - 1,
    randomized.length,
  );
  const windowSize = lastIndex - firstIndex + 1;
  const preferredPosition = firstIndex + (hash(`${seed}:preferred:${preferred.id}`) % windowSize);
  randomized.splice(preferredPosition, 0, preferred);
  return randomized;
};

const palette = [
  { name: "#ff52ec", copy: "#000", message: "#000", messageCopy: "#fff" },
  { name: "#000", copy: "#ff52ec", message: "#ff52ec", messageCopy: "#000" },
  { name: "#db1200", copy: "#000", message: "#fff", messageCopy: "#000" },
  { name: "#fff", copy: "#000", message: "#000", messageCopy: "#fff" },
];

const styleFor = (item: WallItem): CardStyle => {
  const value = hash(item.id);
  const colours = palette[value % palette.length];
  return {
    "--card-rotation": `${((value >>> 4) % 9 - 4) * 0.45}deg`,
    "--card-offset": `${6 + ((value >>> 8) % 18)}%`,
    "--name-bg": colours.name,
    "--name-copy": colours.copy,
    "--message-bg": colours.message,
    "--message-copy": colours.messageCopy,
  };
};

const mergeNewest = (existing: WallItem[], incoming: WallItem[]): WallItem[] => {
  const byId = new Map(existing.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return [...byId.values()].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt) || right.id.localeCompare(left.id));
};

export function MadonnaCardsPage({ report = false }: { report?: boolean }) {
  const [items, setItems] = useState<WallItem[]>([]);
  const [shuffleSeed] = useState(() => `${Date.now()}:${Math.random()}`);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = report ? "All Cards – Report" : "For Madonna";
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const createdRobots = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    const previousRobots = robots.content;
    robots.content = "noindex, nofollow, noarchive";
    return () => {
      document.title = previousTitle;
      if (createdRobots) robots?.remove();
      else if (robots) robots.content = previousRobots;
    };
  }, [report]);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      const loaded: WallItem[] = [];
      let cursor: string | null = null;
      do {
        const page = await getMadonnaCards(cursor);
        loaded.push(...page.items);
        cursor = page.cursor;
      } while (cursor && !cancelled);
      if (!cancelled) setItems(loaded);
    };
    void loadAll().catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const refresh = window.setInterval(() => {
      void getMadonnaCards().then((page) => setItems((current) => mergeNewest(current, page.items))).catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(refresh);
  }, []);

  const orderedItems = useMemo(
    () => report ? randomizeReportCards(items, shuffleSeed) : randomizeCards(items, shuffleSeed),
    [items, report, shuffleSeed],
  );

  const cards = useMemo(() => orderedItems.map((item) => (
    <article
      className={`madonna-card ${item.hasImage ? "madonna-card--photo" : "madonna-card--text"}`}
      key={item.id}
      style={styleFor(item)}
    >
      {item.hasImage && item.imageUrl ? (
        <img
          className={item.imageTreatment === "temporary-flash" ? "madonna-card__image madonna-card__image--temporary" : "madonna-card__image"}
          src={item.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <div className="madonna-card__words">
        <strong className="madonna-card__name">{item.name}</strong>
        <p className="madonna-card__message">{item.message}</p>
      </div>
    </article>
  )), [orderedItems]);

  return (
    <main className="madonna-gallery">
      <header className="madonna-gallery__masthead" aria-label="Messages for Madonna">
        <span aria-label="Everyone here is a work of art">
          EVERYONE HERE IS<br />A WORK OF ART
        </span>
        <h1>MADONNA</h1>
      </header>
      <section className="madonna-gallery__cards" aria-live="off">
        {cards}
      </section>
    </main>
  );
}
